import { ThemeProvider } from '@oneaudi/unified-web-common';
import React, { useEffect, useRef, useState } from 'react';
import { TextButton, Icon, TextLink } from '@oneaudi/unified-web-components';
import { Logo } from '@oneaudi/aoa-shared-editorial-components';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { renderTextWithFootnotesReferences } from '@oneaudi/feature-app-utils';
import uuid from 'react-uuid';
import styled from 'styled-components';
import { getThemeName } from '../../utils/alphaHelper';
import { useWindowSize } from '../../hooks/useWindowSize';
import { withScrollToElement } from '../../componentWrappers/withScrollToElement';
import { scrollToElement } from '../../componentWrappers/withScrollToElement/scrollToElement';
import WLTPComponent from '../MediaDisclaimerAndWLTP/index';
import { useParentSize } from '../../hooks/useParentSize';
import { mediaProperty } from '../../utils/mediaProperty';
import { ResponsiveMedia } from '../ResponsiveMedia/ResponsiveMedia';

import {
  CTAsContainer,
  Container,
  ContentContainer,
  DisclaimerWrapper,
  Heading,
  InnerWrapper,
  LogoContainer,
  MediaContainer,
  StyledHeadline,
  StyledSubHeadline,
  SubHeading,
} from './styles';

const StyledAssetWrapper = styled.div`
  display: grid;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledTextLink = styled(TextLink)`
  ::before {
    display: none;
  }
  svg {
    margin: 0;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => (theme === 'light' && '#333333 !important') || '#e5e5e5 !important'};
`;

const ButtonWithScrollToElement = withScrollToElement(TextButton);
const TextButtonWithScrollToElement = withScrollToElement(StyledTextLink);
const ContainerWithScroll = withScrollToElement(ContentContainer);

const stopPropagation = (e) => {
  const { currentTarget } = e;
  if (currentTarget.getAttribute('href')) {
    e.stopPropagation();
  }
};

export function ContentTile({
  CTA,
  disclaimers,
  darkHeadingsEnabled,
  heading,
  horizontalAlignment,
  horizontalAlignmentSmall,
  icon,
  logo,
  media,
  mobileBreakpointSizes,
  size,
  subHeading,
  theme,
  verticalAlignment,
  verticalAlignmentSmall,
  onClickEvent,
  index,
  layoutName,
  wltpKeys,
  isTest,
  ...ueProps
}) {
  const mediaRef = useRef(null);
  const assetContainerLabel = `${media.imageEnabled ? 'Image' : 'Video'} asset`;
  const assetRef = useRef(null);
  const ctaRef = useRef(null);
  const contentRef = useRef(null);
  const windowSize = useWindowSize();
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const { width, height } = useParentSize(mediaRef);

  useEffect(() => {
    if (!mediaRef || !mediaRef.current) return;

    const videos = mediaRef.current.querySelectorAll('video');
    if (!videos.length) return;

    const video = videos[0];
    const updatePausedState = () => setIsVideoPaused(video.paused);

    updatePausedState();
    video.addEventListener('play', updatePausedState);
    video.addEventListener('pause', updatePausedState);

    return () => {
      video.removeEventListener('play', updatePausedState);
      video.removeEventListener('pause', updatePausedState);
    };
  }, [mediaRef.current]);

  const headingType = (heading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const headingOrder = heading.variant.at(-1);
  const headingWeight = 'normal';
  const subHeadingType = (subHeading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const subHeadingOrder = subHeading.variant.at(-1);
  const subHeadingWeight = 'normal';

  const CTAbutton =
    CTA.variant === 'text' ? TextButtonWithScrollToElement : ButtonWithScrollToElement;

  const CTAprops =
    CTA.variant === 'text'
      ? {
          variant: 'standalone',
          icon: 'forward',
          iconPosition: 'trailing',
        }
      : {
          variant: CTA.variant,
        };

  const ctaLinkType = (linkType) => {
    if (media.assetType === 'image' && (linkType === 'fullTile' || !linkType)) {
      return true;
    }

    return false;
  };

  const isLink = CTA.linkType === 'textOnly';
  const isTileLink = ctaLinkType(CTA.linkType) && CTA.href;
  const isContentLink = isLink && CTA.href;

  const navigateToCTA = (e) => {
    onClickEvent?.(e);

    if (CTA.href.startsWith('#oneLayer')) {
      window.location.href = CTA.href;
      return;
    }

    if (CTA.href.startsWith('#')) {
      scrollToElement(CTA.href, e);
      return;
    }

    if (CTA.openIn === 'New tab') {
      window.open(CTA.href, '_blank');
    } else {
      window.location.href = CTA.href;
    }
  };

  const clickTileContainer = (e) => {
    navigateToCTA(e);
  };

  const handleTileKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      clickTileContainer(e);
    }
  };

  const handleContentKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigateToCTA(e);
    }
  };

  const handleCtaClick = (e) => {
    e.stopPropagation();
    onClickEvent?.(e);
  };

  useEffect(() => {
    if (ctaRef && ctaRef.current && CTA.variant === 'text') {
      const cta = ctaRef.current.querySelector('a');
      cta.setAttribute('aria-label', CTA.ariaLabel);
    }
  }, [ctaRef.current]);

  return (
    <ThemeProvider theme={{ name: theme }}>
      <ContainerWithScroll
        removePadding
        data-testid="content-tile-image-anchor"
        $clickable={ctaLinkType(CTA.linkType)}
      >
        <Container
          data-testid="content-tile-container"
          onClick={isTileLink ? clickTileContainer : undefined}
          onKeyDown={isTileLink ? handleTileKeyDown : undefined}
          role={isTileLink ? 'link' : undefined}
          aria-label={isTileLink ? CTA.ariaLabel || CTA.label : undefined}
          tabIndex={isTileLink ? 0 : undefined}
          index={index}
          size={size}
          layoutName={layoutName}
          mobileBreakpointSizes={mobileBreakpointSizes}
          {...ueProps}
          ref={contentRef}
        >
          <InnerWrapper
            data-testid="content-tile-inner-wrapper"
            verticalAlignment={verticalAlignment}
            verticalAlignmentSmall={verticalAlignmentSmall}
            horizontalAlignment={horizontalAlignment}
            horizontalAlignmentSmall={horizontalAlignmentSmall}
          >
            <MediaContainer ref={mediaRef}>
              <UeContainer propertyPath={`tiles.${index}.mediaAsset`} label={assetContainerLabel}>
                <UeReference propertyPath={`tiles.${index}.mediaAsset`} behavior="component">
                  {(ueMediaProps) => (
                    <StyledAssetWrapper ref={assetRef} {...ueMediaProps}>
                      <UeElement
                        property={
                          mediaProperty(media.assetType, windowSize, isVideoPaused).property
                        }
                        type="media"
                        label={mediaProperty(media.assetType, windowSize, isVideoPaused).label}
                      >
                        <ResponsiveMedia
                          {...media}
                          height={height}
                          width={width}
                          theme={getThemeName(theme)}
                          isTest={isTest}
                        />
                      </UeElement>
                    </StyledAssetWrapper>
                  )}
                </UeReference>
              </UeContainer>
            </MediaContainer>
            <ContentContainer
              {...ueProps}
              data-testid="content-tile-inner-container"
              role={isContentLink ? 'link' : undefined}
              aria-label={isContentLink ? CTA.ariaLabel || CTA.label : undefined}
              tabIndex={isContentLink ? 0 : undefined}
              $clickable={isTileLink || isContentLink}
              onClick={isContentLink ? navigateToCTA : undefined}
              onKeyDown={isContentLink ? handleContentKeyDown : undefined}
            >
              <LogoContainer
                horizontalAlignment={horizontalAlignment}
                horizontalAlignmentSmall={horizontalAlignmentSmall}
              >
                {logo.src && <Logo src={logo.src} alt={logo.alt} heights={logo.heights} />}
                {!logo.src && icon && <StyledIcon theme={theme} name={icon} size="l" />}
              </LogoContainer>
              {heading.text && (
                <Heading
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                >
                  <StyledHeadline
                    darkHeadingsEnabled={darkHeadingsEnabled}
                    tag={heading.htmlTag}
                    variant={{ order: headingOrder, type: headingType, weight: headingWeight }}
                  >
                    <DisclaimerWrapper onClick={stopPropagation}>
                      <UeElement type="text" property="heading_text" label="Heading">
                        {renderTextWithFootnotesReferences(heading.text)}
                      </UeElement>
                    </DisclaimerWrapper>
                  </StyledHeadline>
                </Heading>
              )}
              {subHeading.text && !logo.src && !icon && (
                <SubHeading
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                >
                  <StyledSubHeadline
                    darkHeadingsEnabled={darkHeadingsEnabled}
                    tag={subHeading.htmlTag}
                    variant={{
                      order: subHeadingOrder,
                      type: subHeadingType,
                      weight: subHeadingWeight,
                    }}
                  >
                    <DisclaimerWrapper onClick={stopPropagation}>
                      <UeElement type="text" property="subHeading_text" label="Subheading">
                        {renderTextWithFootnotesReferences(subHeading.text)}
                      </UeElement>
                    </DisclaimerWrapper>
                  </StyledSubHeadline>
                </SubHeading>
              )}
              {CTA.text && (
                <CTAsContainer
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                  ref={ctaRef}
                >
                  <CTAbutton
                    key="cta-button"
                    href={CTA.href}
                    newWindow={CTA.newWindow}
                    data-testid="content-tile-cta"
                    onClick={handleCtaClick}
                    size="sm"
                    target={CTA.openIn === 'New tab' ? '_blank' : '_self'}
                    {...CTAprops}
                    aria-label={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
                  >
                    <UeElement type="text" property="CTA_text" label="CTA">
                      {CTA.text}
                    </UeElement>
                  </CTAbutton>
                </CTAsContainer>
              )}
            </ContentContainer>
          </InnerWrapper>
        </Container>
      </ContainerWithScroll>
      <WLTPComponent
        theme={getThemeName(theme)}
        materialDisclaimer={disclaimers}
        wltpTextValues={wltpKeys}
      />
    </ThemeProvider>
  );
}

ContentTile.displayName = 'ContentTile';
